import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./explore.html";
import { WireStore } from '@oneappexchange/lwc-wired';
import { Variant as ButtonVariant } from 'wds/button';
import equalityFn from 'zustand/shallow';
import { BUCKETS } from '~/modules/config';
import { FeatureFlags } from '~/modules/feature_flags';
import { totalFilterSearchResults } from '~/modules/helpers/filterHelper';
import { asyncGetFocusTrap } from '~/modules/helpers/focusTrap';
import { SectionNames, sortOptionsForType } from '~/modules/helpers/searchSortOptionsHelper';
import { buildExploreNavigationUrl, buildNavigationUrl } from '~/modules/helpers/searchUrlHelper';
import { RESULT_STATUS, exploreStore, getLoadingBucketResult, selectBucketResults } from '~/modules/stores/exploreStore';
import { WireI18n, i18nStore, selectExplorePageLabels } from '~/modules/stores/i18nStore';
import { navigate } from '~/modules/stores/routerStore';
import { filtersStore, selectFilterChipsByType, selectFiltersByExploreType } from '~/modules/stores/searchFiltersStore';
import { exportPageSelector, searchStore } from '~/modules/stores/searchStore';
import { DeviceType, selectDeviceType, viewportStore } from '~/modules/stores/viewportStore';
import { randomUUID } from '~/modules/utilities/cross_env_utils';
class ExplorePage extends LightningElement {
  constructor(...args) {
    super(...args);
    this.bucketStatus = RESULT_STATUS.LOADING;
    this.sections = [];
    this.displayMobileFilterPanel = false;
    this.currentKeyTotalResults = undefined;
    this.deviceType = void 0;
    this.isLoading = true;
    this.uniqueFiltersSelected = 0;
    this.isSearchError = false;
    this.hideSortButton = false;
    this.filterSections = [];
    this.filterKeyword = undefined;
    this.filterChips = [];
    this.handleRetry = () => {
      exploreStore.fetchBucketSections(this.bucket, true);
    };
  }
  i18nCallback(i18n) {
    this.labels = i18n.explore;
    this.searchLabels = i18n.search;
  }
  setCategoryResults({
    currentBucket,
    bucketResult
  }) {
    this.bucket = currentBucket;
    this.setBucketResults(bucketResult || getLoadingBucketResult());
  }
  setBucketResults(bucketResult) {
    this.bucketStatus = bucketResult.status;
    this.sections = bucketResult.sections.map(section => ({
      ...section,
      listings: section.listings.slice(0, 2)
    }));
  }
  filterUpdate([sections, filterKeyword, chips]) {
    this.filterSections = sections.map(section => ({
      ...section,
      label: undefined,
      noSectionToggle: true
    }));
    this.filterKeyword = filterKeyword;
    this.filterChips = chips;
    this.uniqueFiltersSelected = chips.length;
    this.fetchSectionsDependingOnFilters();
  }
  setSearchTotals({
    searchParams,
    results
  }) {
    this.searchParams = searchParams;
    this.currentKeyTotalResults = results?.totalSearchResults ?? -1;
    this.isLoading = results?.isLoading;
    this.isSearchError = results?.isError;
    this.hideSortButton = this.isSearchError || !this.isLoading && this.currentKeyTotalResults <= 0;
  }
  get shouldDisplayFilters() {
    return !this.mobileFilterPanel || this.displayMobileFilterPanel;
  }
  handleFilterSelectionChange(event) {
    const selectedOption = event.detail;
    this.changeFilter(selectedOption.section, selectedOption.value);
  }
  changeFilter(section, value) {
    const url = this.buildFilterUrl(section, value);
    navigate(url);
  }
  buildFilterUrl(filterKey, filterValue) {
    return buildExploreNavigationUrl(this.bucket, this.searchParams, {
      filter: {
        key: filterKey,
        value: filterValue
      },
      type: this.searchParams.type || 'apps'
    });
  }
  get agentforcePage() {
    return this.bucket === BUCKETS.EXPLORE_AGENTFORCE;
  }
  handleFilterAnalytics(event) {
    const url = this.buildFilterUrl(event.detail.section, [event.detail.value]);
    const filterInputResultCount = totalFilterSearchResults(this.filterSections);
    event.detail.searchContext = {
      url,
      keyword: '',
      sectionLabel: event.detail.sectionLabel || event.detail.section,
      optionLabel: event.detail.value,
      filterKeyword: this.filterKeyword,
      searchResultCount: filterInputResultCount,
      searchResults: this.currentKeyTotalResults,
      bucket: this.bucket
    };
  }
  handleSortSelectionChange(event) {
    const {
      value: [selectedOption]
    } = event.detail;
    const url = buildExploreNavigationUrl(this.bucket, this.searchParams, {
      sort: selectedOption
    });
    navigate(url);
  }
  handleAnalytics(event) {
    event.detail.title = this.bucket;
    if (['link', 'button', 'filter'].includes(event.detail.elementType)) {
      event.detail.gtm = 'ga4';
    }
  }
  get mobileFilterChips() {
    return this.filterChips?.length > 0 && this.mobileFilterPanel;
  }
  get filterByLabel() {
    const {
      i18n
    } = i18nStore.getState();
    return i18n.search.keywordlessFilteredBy({
      showing: this.currentKeyTotalResults || 0
    });
  }
  get filterByAria() {
    return this.filterChips.map(chip => chip.label).join(', ');
  }
  get showFilterChips() {
    return this.filterChips?.length > 0 && this.isDesktop;
  }
  get filterContainerClass() {
    return this.showFilterChips ? 'filter-container' : 'filter-container no-chips';
  }
  get filterHeaderClass() {
    return this.showFilterChips ? 'explore-filter-header' : 'explore-filter-header no-chips';
  }
  get isMobilePhone() {
    return this.deviceType === DeviceType.PHONE;
  }
  get isDesktop() {
    return this.deviceType === DeviceType.DESKTOP;
  }
  get buttonVariant() {
    return this.isMobilePhone ? ButtonVariant.Secondary : ButtonVariant.Tertiary;
  }
  get sortOptions() {
    return sortOptionsForType(this.searchParams?.type || 'apps', this.searchLabels.sort);
  }
  get selectedSortOption() {
    return this.searchParams?.sort || 'relevant';
  }
  handleChipClosed(event) {
    const filterToRemove = event.detail;
    if (filterToRemove.section === 'location') {
      this.closeLocationChip(filterToRemove);
    } else {
      // @ts-expect-error * Automated Type Error Fix
      const oldValues = [this.searchParams.filterParams[filterToRemove.section]].flat();
      const newValues = oldValues.filter(value => value !== filterToRemove.value);
      this.changeFilter(filterToRemove.section, newValues);
    }
  }

  // @ts-expect-error * Automated Type Error Fix
  closeLocationChip(filterToRemove) {
    // @ts-expect-error * Automated Type Error Fix
    const oldValue = this.searchParams.filterParams[filterToRemove.section];
    const [country, state] = oldValue.split('-');
    if (country === filterToRemove.value) {
      // @ts-expect-error * Automated Type Error Fix
      this.changeFilter(filterToRemove.section, undefined);
    }
    if (state === filterToRemove.value) {
      this.changeFilter(filterToRemove.section, [`${country}-*`]);
    }
  }
  switchMobileFilterPanel() {
    this.switchFocus(!this.displayMobileFilterPanel);
    this.displayMobileFilterPanel = !this.displayMobileFilterPanel;
  }
  async switchFocus(isPanelOpen) {
    if (isPanelOpen) {
      const filters = await asyncGetFocusTrap(this.template, '.filters');
      filters?.focusFirstElement();
    } else {
      this.template.querySelector('.add-filter-button').focus();
    }
  }
  get pageTitle() {
    const sectionLabel = this.bucket ? this.labels.buckets[this.bucket] : '';
    return i18nStore.getState().i18n.explore.pageTitle({
      SECTION: sectionLabel
    });
  }
  get categorySections() {
    return this.sections?.map(section => {
      const title = section.label;
      return {
        ...section,
        props: {
          format: 'listing',
          id: randomUUID(),
          title,
          showMore: {
            label: i18nStore.getState().i18n.explore.section.moreIn({
              SECTION: title
            }),
            url: section.url
          }
        }
      };
    });
  }
  get showBucketResults() {
    return !this.isBucketError && !this.isInitialLoading;
  }
  get isBucketError() {
    return this.bucketStatus === RESULT_STATUS.ERROR;
  }
  get mobileFilterPanel() {
    return this.deviceType !== DeviceType.DESKTOP;
  }
  get contentGap() {
    return !this.mobileFilterPanel ? 'x-large' : 'none';
  }
  get isInitialLoading() {
    return this.bucketStatus && this.bucketStatus === RESULT_STATUS.LOADING;
  }
  get addFilterButtonText() {
    const {
      i18n
    } = i18nStore.getState();
    if (this.uniqueFiltersSelected > 0) {
      return i18n.search.filtersButtonWithAmount({
        amount: this.uniqueFiltersSelected
      });
    }
    return this.searchLabels.addFiltersButton;
  }
  get closeButtonText() {
    const {
      i18n
    } = i18nStore.getState();
    return this.showSearchResults ? i18n.search.showFilterResults({
      amount: this.currentKeyTotalResults
    }) : this.searchLabels.closeFilterPanel;
  }
  get showSearchResults() {
    return this.filterSections?.some(filter => filter.selectedValue?.length > 0) || this.agentforcePage;
  }
  handleSwitch(event) {
    const type = event.detail.type;
    const url = buildExploreNavigationUrl(this.bucket, this.searchParams, {
      type: type,
      resetState: true
    });
    navigate(url);
  }
  getExpandedSections() {
    switch (this.bucket) {
      case BUCKETS.EXPLORE_BUSINESS_NEEDS:
        return SectionNames.CATEGORY;
      case BUCKETS.EXPLORE_IMPACT:
        return SectionNames.IMPACT;
      case BUCKETS.EXPLORE_INDUSTRIES:
        return SectionNames.INDUSTRY;
      case BUCKETS.EXPLORE_LABS:
        return [SectionNames.SALESFORCE_LABS, SectionNames.TECH];
      case BUCKETS.EXPLORE_PRODUCTS:
        return SectionNames.PRODUCT;
      default:
        return this.bucket;
    }
  }
  handleShowAdvancedFilters() {
    const params = {
      ...this.searchParams,
      filterParams: {
        ...this.searchParams.filterParams
      }
    };
    params.keyword = '';
    if (this.bucket === BUCKETS.EXPLORE_LABS) {
      params.filterParams.labs = ['salesforceLabs'];
    }
    filtersStore.setOpenFilterPanel(true);
    const url = buildNavigationUrl(params, {
      type: params.type,
      expand: this.getExpandedSections()
    });
    navigate(url);
  }
  handleBucketClick(event) {
    this.switchBucket(event.detail.value);
  }
  get bucketSectionOptions() {
    const labels = this.searchLabels.filters;
    const buckets = [{
      label: labels.explore.businessNeeds,
      value: 'business-needs',
      icon: 'business-needs',
      selected: this.isSelectedBucket('business-needs')
    }, {
      label: labels.explore.products,
      value: 'products',
      icon: 'products',
      selected: this.isSelectedBucket('products')
    }, {
      label: labels.explore.industries,
      value: 'industries',
      icon: 'industries',
      selected: this.isSelectedBucket('industries')
    }, {
      label: labels.explore.labs,
      value: 'salesforce-labs',
      icon: 'labs',
      selected: this.isSelectedBucket('salesforce-labs')
    }, {
      label: labels.explore.impact,
      value: 'impact',
      icon: 'impact',
      selected: this.isSelectedBucket('impact')
    }];
    if (FeatureFlags.FF_EXPLORE_AGENTFORCE_PAGE) {
      buckets.splice(1, 0, {
        label: labels.explore.agentforce,
        value: 'agentforce',
        icon: 'agentforce',
        selected: this.isSelectedBucket('agentforce')
      });
    }
    return buckets;
  }
  isSelectedBucket(anotherBucket) {
    return this.bucket === anotherBucket;
  }
  switchBucket(newBucket) {
    const url = buildExploreNavigationUrl(newBucket, this.searchParams, {
      type: this.searchParams.type || 'apps',
      resetState: true
    });
    navigate(url);
  }
  fetchSectionsDependingOnFilters() {
    if (!this.showSearchResults) {
      exploreStore.fetchBucketSections(this.bucket);
    }
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(ExplorePage, {
  wire: {
    deviceType: {
      adapter: WireStore,
      dynamic: [],
      config: function ($cmp) {
        return {
          store: viewportStore,
          selector: selectDeviceType
        };
      }
    },
    i18nCallback: {
      adapter: WireI18n,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          selector: selectExplorePageLabels
        };
      }
    },
    setCategoryResults: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: exploreStore,
          selector: selectBucketResults
        };
      }
    },
    filterUpdate: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: filtersStore,
          selector: state => [selectFiltersByExploreType(state), state.filterKeyword, selectFilterChipsByType()],
          equalityFn
        };
      }
    },
    setSearchTotals: {
      adapter: WireStore,
      dynamic: [],
      method: 1,
      config: function ($cmp) {
        return {
          store: searchStore,
          selector: exportPageSelector
        };
      }
    }
  },
  fields: ["bucketStatus", "sections", "displayMobileFilterPanel", "currentKeyTotalResults", "isLoading", "uniqueFiltersSelected", "isSearchError", "hideSortButton", "filterSections", "filterKeyword", "filterChips", "handleRetry"]
});
const __lwc_component_class_internal = _registerComponent(ExplorePage, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;